import { Stat } from "@/pages/analytics/TrafficPanel";
import { GraduationCap, Users, School, UserPlus, Activity } from "lucide-react";

const Row = ({ label, g, e, total, testid }) => (
  <div className="flex items-center justify-between gap-4 px-4 py-3 border-t border-white/5 text-sm" data-testid={testid}>
    <span className="text-slate-200">{label}</span>
    <span className="flex items-center gap-4 font-mono-data">
      <span className="text-[#22D3EE]">{g} guides</span>
      <span className="text-primary">{e} explorers</span>
      <span className="text-slate-100 w-10 text-right">{total}</span>
    </span>
  </div>
);

export function OverviewPanel({ data, days }) {
  const p = data.people;
  const s = data.signed_in;
  const n = data.new_signups;
  const w = data.work;
  const maxTrack = Math.max(1, ...data.tracks.map((t) => t.missions_worked));

  return (
    <div className="space-y-6" data-testid="overview-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Guides" value={p.guides} testid="stat-guides" />
        <Stat label="Explorers" value={p.explorers} accent="text-primary" testid="stat-explorers" />
        <Stat label="Classes" value={p.classes} accent="text-slate-100" testid="stat-classes" />
        <Stat label={`Signed in today`} value={s.today.total} accent="text-[#34D399]" testid="stat-signed-in-today" />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="hq-glass rounded-2xl border border-white/10 overflow-hidden">
          <h3 className="font-display text-xl px-4 py-3 flex items-center gap-2"><Activity className="w-5 h-5 text-[#34D399]" /> Who signed in</h3>
          <Row label="Today" g={s.today.guides} e={s.today.explorers} total={s.today.total} testid="signed-in-today" />
          <Row label="Last 7 days" g={s.last_7d.guides} e={s.last_7d.explorers} total={s.last_7d.total} testid="signed-in-7d" />
          <Row label="Last 30 days" g={s.last_30d.guides} e={s.last_30d.explorers} total={s.last_30d.total} testid="signed-in-30d" />
          <p className="text-xs text-muted-foreground px-4 py-3 border-t border-white/5">Counted from real sign-ins. Your own owner account is never counted.</p>
        </div>

        <div className="hq-glass rounded-2xl border border-white/10 overflow-hidden">
          <h3 className="font-display text-xl px-4 py-3 flex items-center gap-2"><UserPlus className="w-5 h-5 text-[#22D3EE]" /> New in the last {days} days</h3>
          <Row label="New accounts" g={n.guides} e={n.explorers} total={n.guides + n.explorers} testid="new-signups" />
          <div className="flex items-center justify-between px-4 py-3 border-t border-white/5 text-sm" data-testid="trial-requests">
            <span className="text-slate-200">Free-trial sign-ups</span><span className="font-mono-data text-primary">{n.trial_requests}</span>
          </div>
          <div className="grid grid-cols-2 gap-x-4 px-4 py-3 border-t border-white/5 text-sm">
            <span className="text-slate-200 flex items-center gap-2"><GraduationCap className="w-4 h-4 text-muted-foreground" /> Full Guide accounts</span>
            <span className="font-mono-data text-right text-[#34D399]" data-testid="guides-full">{p.guides_full}</span>
            <span className="text-slate-200 flex items-center gap-2"><Users className="w-4 h-4 text-muted-foreground" /> Guides on a trial</span>
            <span className="font-mono-data text-right text-primary" data-testid="guides-trial">{p.guides_trial}</span>
            <span className="text-slate-200 flex items-center gap-2"><School className="w-4 h-4 text-muted-foreground" /> Trial classes</span>
            <span className="font-mono-data text-right text-slate-300" data-testid="classes-trial">{p.classes_trial}</span>
          </div>
        </div>
      </div>

      <div className="hq-glass rounded-2xl border border-white/10 p-5">
        <h3 className="font-display text-xl mb-1">Busiest tracks · last {days} days</h3>
        <p className="text-xs text-muted-foreground mb-4">Lessons and drills Explorers actually worked on, with how many of them reached mastery.</p>
        {data.tracks.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">No Studio work recorded in this range yet.</p> : (
          <div className="space-y-2" data-testid="track-usage">
            {data.tracks.map((t) => (
              <div key={t.track || "none"} className="flex items-center gap-3 text-sm" data-testid={`track-row-${t.track}`}>
                <div className="w-36 truncate text-slate-200">{t.name}</div>
                <div className="flex-1 h-5 rounded bg-white/5 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#22D3EE] to-primary transition-all duration-500" style={{ width: `${Math.round((t.missions_worked / maxTrack) * 100)}%` }} />
                </div>
                <div className="w-52 text-right text-xs text-slate-400">{t.missions_worked} worked · {t.mastered} mastered · {t.explorers} explorers</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label={`Sign-ins · ${days}d`} value={w.logins} testid="stat-logins-range" />
        <Stat label={`Lessons worked · ${days}d`} value={w.missions_worked} accent="text-primary" testid="stat-missions-range" />
        <Stat label={`MC tests · ${days}d`} value={w.tests_taken} accent="text-[#34D399]" testid="stat-tests-range" />
        <Stat label={`Hands-on tests · ${days}d`} value={w.unit_tasks_taken} accent="text-slate-100" testid="stat-unit-tasks-range" />
      </div>
    </div>
  );
}
